import React, {useMemo} from 'react';

import {ResponsivePie} from '@nivo/pie';
import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';

import {Spinner} from 'components/ui-kit/Spinner';
import {activeStatusBarChartChanged, dashboardState$} from 'models/dashboard';
import {colors} from 'theme';
import {ProductStatusRecord} from 'types';

import {formatNumber} from 'utils/number';

import {BlockTitle, SpinnerLayer} from '../styled';

import {ProductStatusLegend} from './ProductStatusLegend';
import {Root} from './styled';

type DefectChartColorsOptions = {
    [key: string]: string;
};

export const DefectChartColors: DefectChartColorsOptions = {
    GENERATED: '#FA7921FF',
    DROPOUT: '#72B46AFF',
    INTRODUCED: '#9F6EC4FF',
    SOLD: colors.brandPrimary,
    UTILISATION: '#FFD166FF',
};

type Props = {
    data: ProductStatusRecord[];
    className?: string;
};

const ChartContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    margin-top: 20px;
    align-items: center;
`;

const SvgContainer = styled.div`
    height: 300px !important;
    width: 300px !important;
`;

const LegendContainer = styled.div`
    flex: 1;
    width: 100%;
`;

const CustomRoot = styled(Root)`
    height: auto;
`;

const TooltipContainer = styled.div<{color?: string}>`
    display: grid;
    gap: 6px;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
    padding: 8px;
    border-radius: 6px;
    background: ${colors.white};
    box-shadow: 0 0 10px 4px rgba(0, 0, 0, 0.25);

    & > h3 {
        grid-column: 1 / -1;
        font-size: 12px;
        font-weight: 500;
        color: #8a8a8a;
        margin: 0;
    }

    & > div {
        width: 16px;
        aspect-ratio: 1;
        border-radius: 4px;
        background: ${(p) => p.color ?? 'transparent'};
    }

    & > span {
        font-size: 13px;
        font-weight: 500;
        color: #303030;
    }
`;

export function ProductStatusChart({data, className}: Props) {
    const {t, i18n} = useTranslation();
    const {productStatusPending} = useStore(dashboardState$);

    const mappedData = useMemo(
        () =>
            data.map((item) => ({
                ...item,
                id: item.status,
                label: t(
                    `dictionaries.products.status.${
                        item.status === 'SOLD' ? 'SOLD_AT_RETAIL' : item.status
                    }`,
                ),
                color: DefectChartColors[item.status],
            })),
        [data, i18n.language],
    );

    return (
        <CustomRoot className={className}>
            <BlockTitle>{t('pages.dashboard.statusChart')}</BlockTitle>
            {productStatusPending ? (
                <SpinnerLayer>
                    <Spinner />
                </SpinnerLayer>
            ) : (
                <ChartContainer>
                    {mappedData.some((d) => d.value) && (
                        <SvgContainer>
                            <ResponsivePie
                                data={mappedData.filter((item, _, array) => {
                                    const maxValue = Math.max(...array.map((d) => d.value));
                                    return item.value > 0 && item.value > maxValue / 100;
                                })}
                                margin={{top: 30, right: 0, bottom: 30, left: 0}}
                                innerRadius={0.9}
                                padAngle={1}
                                cornerRadius={30}
                                colors={{datum: 'data.color'}}
                                borderWidth={17}
                                enableArcLabels={false}
                                enableArcLinkLabels={false}
                                valueFormat={(value) => formatNumber(value, false, 2)}
                                tooltip={(slice) => (
                                    <TooltipContainer color={slice.datum.color}>
                                        <h3>{slice.datum.label}</h3>
                                        <div />
                                        <span>
                                            {formatNumber(slice?.datum?.value ?? 0, false, 2)}
                                        </span>
                                    </TooltipContainer>
                                )}
                                borderColor={{from: 'color', modifiers: [['darker', 0.2]]}}
                                onMouseEnter={(node, event) => {
                                    const target = event.target as SVGPathElement;
                                    activeStatusBarChartChanged(node.label as string);
                                    target.style.transition = 'all 0.3s';
                                    target.style.strokeWidth = '20';
                                    target.style.stroke = node.color;
                                }}
                                onMouseLeave={(_, event) => {
                                    const target = event.target as SVGPathElement;
                                    activeStatusBarChartChanged(null);
                                    target.style.strokeWidth = '17';
                                }}
                            />
                        </SvgContainer>
                    )}
                    <LegendContainer>
                        <ProductStatusLegend data={mappedData} />
                    </LegendContainer>
                </ChartContainer>
            )}
        </CustomRoot>
    );
}
